import { supabase, N8N_BASE_URL } from '@/lib/supabase';
import { getUserId } from '@/api/auth';
import { fetchMemberAssetsSafe, confirmAssetSafe } from '@/api/memberSafe';
import { safeAssetToMaterialRow } from '@/lib/member/safeAssets';
import type { SceneType, PhotoSubtype, SilentMotion } from '@/lib/materialScenes';
import { DEFAULT_TALKING_SCRIPT } from '@/lib/materialScenes';
import { executeDailyMaterialPipeline, executeSelectedMaterials } from '@/api/commercialPipeline';

export interface IdentityProfile {
  character_id: number;
  identity_profile_id?: string;
  identity_lock_level?: string;
  identity_type: string;
  face_lock: Record<string, unknown>;
  body_lock: Record<string, unknown>;
  visual_style: Record<string, unknown>;
  updated_at?: string;
}

export interface VoiceProfileRow {
  character_id: number;
  voice_profile_id?: string;
  voice_id: string | null;
  heygen_voice_id: string | null;
  voice_source: string | null;
  voice_status: string;
  voice_profile: Record<string, unknown>;
  updated_at?: string;
}

export interface AuthorizationProfile {
  character_id: number;
  face_authorized: boolean;
  body_authorized: boolean;
  voice_authorized: boolean;
  authorization_method: string | null;
  consent_record: Record<string, unknown>;
  created_at?: string;
  revoked_at?: string | null;
}

export interface MaterialJob {
  id: number;
  job_id: string | null;
  character_id: number;
  user_id: string | null;
  material_type: string;
  scene_type: string | null;
  input: Record<string, unknown>;
  provider: string | null;
  status: string;
  result: Record<string, unknown>;
  asset_id: number | null;
  created_at: string;
  completed_at: string | null;
}

export interface MaterialAsset {
  id: number;
  created_at: string;
  character_id: number | null;
  asset_type: 'image' | 'video' | 'audio';
  url: string | null;
  status: string;
  material_type: string | null;
  scene_type: string | null;
  usage_purpose: string | null;
  photo_subtype: string | null;
  motion: string | null;
}

export interface LeadHookAsset {
  id: number;
  character_id: number;
  related_asset_id: number | null;
  hook_type: string;
  target_user: string | null;
  pain_point: string | null;
  public_trigger: string | null;
  comment_keyword: string | null;
  public_reply: string | null;
  dm_opening: string | null;
  follow_up_question: string | null;
  next_step: string | null;
  asset_content: Record<string, unknown>;
  created_at: string;
}

export interface ReplyMaterial {
  id: number;
  character_id: number;
  source_asset_id: number | null;
  source_hook_id: number | null;
  scene: string | null;
  target_user_type: string | null;
  reply_type: string;
  content: string | null;
  next_question: string | null;
  lead_asset: string | null;
  usage_count: number;
  effect_feedback: Record<string, unknown>;
  meta?: Record<string, unknown>;
  created_at: string;
}

export interface DailyMaterialPack {
  id: number;
  pack_id: string;
  character_id: number;
  pack_type: string;
  pack_goal: string | null;
  target_user_type: string | null;
  materials: DailyPackItem[];
  status: string;
  created_at: string;
  completed_at: string | null;
}

export interface DailyPackItem {
  material_request_id: string;
  material_type: string;
  scene: string;
  usage_scene?: string;
  business_purpose?: string;
  status: string;
  count?: number;
}

export interface MaterialUsageRow {
  id: number;
  character_id: number;
  source_type: string;
  source_id: number | null;
  platform: string | null;
  result: string | null;
  note: string | null;
  next_recommendation: string | null;
  effect_feedback: Record<string, unknown>;
  created_at: string;
}

export async function ensureIdentityProfile(characterId: number): Promise<IdentityProfile> {
  const { data, error } = await supabase.rpc('ensure_character_identity_profile', { p_character_id: characterId });
  if (error) throw error;
  return data as IdentityProfile;
}

export async function ensureVoiceProfile(characterId: number): Promise<VoiceProfileRow> {
  const { data, error } = await supabase.rpc('ensure_character_voice_profile', { p_character_id: characterId });
  if (error) throw error;
  return data as VoiceProfileRow;
}

export async function getAuthorizationProfile(characterId: number): Promise<AuthorizationProfile | null> {
  const { data, error } = await supabase
    .from('character_authorization_profiles')
    .select('*')
    .eq('character_id', characterId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function upsertAuthorizationProfile(
  characterId: number,
  patch: Partial<Pick<AuthorizationProfile, 'face_authorized' | 'body_authorized' | 'voice_authorized'>>,
): Promise<void> {
  const { error } = await supabase.from('character_authorization_profiles').upsert({
    character_id: characterId,
    ...patch,
    authorization_method: 'user_checkbox_and_record',
    consent_record: { updated_at: new Date().toISOString() },
  });
  if (error) throw error;
}

export async function getMaterialJobs(characterId: number, limit = 30): Promise<MaterialJob[]> {
  const { data, error } = await supabase
    .from('character_material_jobs')
    .select('*')
    .eq('character_id', characterId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

/** 角色素材库内的照片/无声影片（meta.material_library = true）— 会员 safe DTO */
export async function getMaterialAssets(characterId: number): Promise<MaterialAsset[]> {
  const rows = await fetchMemberAssetsSafe({
    characterId,
    materialLibraryOnly: true,
  });
  return rows.map((r) => safeAssetToMaterialRow(r) as MaterialAsset);
}

export async function getLeadHooks(characterId: number): Promise<LeadHookAsset[]> {
  const { data, error } = await supabase
    .from('lead_hook_assets')
    .select('*')
    .eq('character_id', characterId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function getReplyMaterials(characterId?: number): Promise<ReplyMaterial[]> {
  let q = supabase
    .from('reply_material_library')
    .select('*')
    .order('created_at', { ascending: false });
  if (characterId) q = q.eq('character_id', characterId);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export async function ensureDailyMaterialPack(characterId: number): Promise<DailyMaterialPack> {
  const { data, error } = await supabase.rpc('ensure_daily_material_pack', { p_character_id: characterId });
  if (error) throw error;
  return data as DailyMaterialPack;
}

export async function updateDailyPackItem(
  packId: string,
  requestId: string,
  status: string,
  result?: Record<string, unknown>,
): Promise<DailyMaterialPack> {
  const { data, error } = await supabase.rpc('update_daily_pack_item', {
    p_pack_id: packId,
    p_request_id: requestId,
    p_status: status,
    p_result: result ?? {},
  });
  if (error) throw error;
  return data as DailyMaterialPack;
}

export async function setDailyPackStatus(packId: string, status: string): Promise<void> {
  const { error } = await supabase.rpc('set_daily_pack_status', { p_pack_id: packId, p_status: status });
  if (error) throw error;
}

export async function recordMaterialUsage(input: {
  character_id: number;
  source_type: 'asset' | 'hook' | 'reply' | 'pack';
  source_id: number;
  platform: string;
  result: string;
  note?: string;
  next_recommendation?: string;
}): Promise<number> {
  const { data, error } = await supabase.rpc('record_material_usage', {
    p_character_id: input.character_id,
    p_source_type: input.source_type,
    p_source_id: input.source_id,
    p_platform: input.platform,
    p_result: input.result,
    p_note: input.note ?? null,
    p_next_recommendation: input.next_recommendation ?? null,
  });
  if (error) throw error;
  return data as number;
}

export async function updateBodyLock(characterId: number, bodyType: string, bodyRatio = '自然比例'): Promise<IdentityProfile> {
  const { data, error } = await supabase.rpc('update_character_body_lock', {
    p_character_id: characterId,
    p_body_type: bodyType,
    p_body_ratio: bodyRatio,
  });
  if (error) throw error;
  return data as IdentityProfile;
}

export async function getMaterialUsageHistory(characterId: number, limit = 30): Promise<MaterialUsageRow[]> {
  const { data, error } = await supabase
    .from('material_usage')
    .select('*')
    .eq('character_id', characterId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export type TextMaterialType = 'moments' | 'dm_opener' | 'comment_icebreaker';

export async function generateMaterialText(
  characterId: number,
  textType: TextMaterialType,
  count?: number,
): Promise<{ ok?: boolean; message?: string; count?: number }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_material_text`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId, text_type: textType, count }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

export async function incrementReplyUsage(replyId: number): Promise<void> {
  const { error } = await supabase.rpc('increment_reply_material_usage', { p_id: replyId });
  if (error) throw error;
}

export interface GeneratePhotoInput {
  character_id: number;
  scene_type: SceneType;
  photo_subtype: PhotoSubtype;
  usage?: string;
  identity_lock?: boolean;
  body_lock?: boolean;
}

export async function generateMaterialPhoto(input: GeneratePhotoInput): Promise<{ ok?: boolean; job_id?: string; url?: string; asset_id?: number; message?: string }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const userId = await getUserId().catch(() => null);
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_material_photo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...input, user_id: userId }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

export interface GenerateSilentVideoInput {
  character_id: number;
  scene_type: SceneType;
  motion: SilentMotion;
  usage?: string;
}

export async function generateMaterialSilentVideo(input: GenerateSilentVideoInput): Promise<{ ok?: boolean; started?: boolean; job_id?: string; message?: string }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const userId = await getUserId().catch(() => null);
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_material_silent_video`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...input, user_id: userId }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

export interface GenerateTalkingVideoInput {
  character_id: number;
  script?: string;
  usage?: string;
}

export async function generateMaterialTalkingVideo(input: GenerateTalkingVideoInput): Promise<{ ok?: boolean; started?: boolean; message?: string }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const userId = await getUserId().catch(() => null);
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_material_talking`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      character_id: input.character_id,
      script: input.script || DEFAULT_TALKING_SCRIPT,
      usage: input.usage || 'douyin',
      user_id: userId,
      material_library: true,
    }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

/** 一键生成今日素材包：走 Commercial Pipeline（兼容旧入口） */
export async function generateDailyMaterialPack(characterId: number): Promise<{ ok: boolean; message: string }> {
  const pack = await ensureDailyMaterialPack(characterId);
  await setDailyPackStatus(pack.pack_id, 'generating');
  const result = await executeDailyMaterialPipeline(characterId, pack.pack_id);
  await setDailyPackStatus(pack.pack_id, result.ok ? 'partial' : 'failed');
  return { ok: result.ok, message: result.message };
}

/** 勾选确认后生成：只执行 selectedKeys */
export async function generateSelectedDailyMaterials(
  characterId: number,
  selectedKeys: string[],
  onProgress?: (key: string, phase: 'start' | 'done' | 'fail') => void,
): Promise<{ ok: boolean; message: string }> {
  const pack = await ensureDailyMaterialPack(characterId);
  await setDailyPackStatus(pack.pack_id, 'generating');
  const result = await executeSelectedMaterials(characterId, pack.pack_id, selectedKeys, {
    onJobStart: (_o, _t, key) => { if (key) onProgress?.(key, 'start'); },
    onJobDone: (_o, _t, ok, key) => { if (key) onProgress?.(key, ok ? 'done' : 'fail'); },
  });
  await setDailyPackStatus(pack.pack_id, result.ok ? 'partial' : 'failed');
  return { ok: result.ok, message: result.message };
}

export async function generateLeadHook(
  characterId: number,
  relatedAssetId?: number,
  context?: { usage?: string; topic?: string },
): Promise<{ ok?: boolean; hook_id?: number; hook?: LeadHookAsset }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_lead_hook`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      character_id: characterId,
      related_asset_id: relatedAssetId ?? null,
      usage: context?.usage,
      topic: context?.topic,
    }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

export async function recordHookFeedback(
  hookId: number,
  feedback: string,
  characterId: number,
  platform = 'dm',
): Promise<void> {
  await recordMaterialUsage({
    character_id: characterId,
    source_type: 'hook',
    source_id: hookId,
    platform,
    result: feedback,
  });
}

export async function recordReplyFeedback(
  replyId: number,
  characterId: number,
  result: string,
  platform = 'dm',
  note?: string,
): Promise<void> {
  await recordMaterialUsage({
    character_id: characterId,
    source_type: 'reply',
    source_id: replyId,
    platform,
    result,
    note,
  });
}

export async function confirmMaterialAsset(assetId: number, characterId?: number): Promise<void> {
  await confirmAssetSafe(assetId, {
    characterId: characterId ?? null,
    materialLibraryOnly: true,
    statuses: ['ready', 'pending_review', 'generating'],
  });
}

export async function rejectMaterialAsset(assetId: number): Promise<void> {
  const { error } = await supabase.rpc('reject_asset', { p_asset_id: assetId });
  if (error) throw error;
}
